import React, { useEffect, useRef, useState } from "react";
import { Button, Grid, Paper, Stack, TextField, Typography } from "@mui/material";

import ActivityTableComponent from "../../../components/admin/activity-manager/ActivityTable";
import { ActivityAddDrawer } from "../../../components/admin/activity-manager/ActivityAddDrawer";

import { useGetActivityListQuery } from "../../../store/activity/activityApis";
import { defaultPaginate, PERMISSIONS } from "../../../helpers/constants";
import { GenerateBookingSlot } from "../../../components/admin/activity-manager/GenerateBookingSlot";
import HasPermission from "../../../components/Common/HasPermission";
import AutoCompleteSelect from "../../../components/Common/AutoCompleteSelect";
import { useGetParentCategoryListQuery, useGetParentLocationListQuery } from "../../../store/masters/mastersApis";
import { debounce } from "../../../helpers/utils";

const ActivityManagerContainer = (props) => {
    const { showDrawer, formType, initialValues, pagination } = props;
    const { isLoading, data: activities, isFetching } = useGetActivityListQuery({ ...pagination });
    const [showGenerateSlot, setShowGenerateSlot] = useState(false);
    const [searchText, setSearchText] = useState("");
    const { data: categoryOptions = [] } = useGetParentCategoryListQuery({});
    const { data: locationOptions = [] } = useGetParentLocationListQuery({});
    const debouncedSearchRef = useRef();

    if (!debouncedSearchRef.current) {
        debouncedSearchRef.current = debounce((value, currentPagination) => {
            props.handlePaginationState({
                ...currentPagination,
                pageNo: 0,
                keywords: value.toLowerCase(),
            });
        });
    }

    useEffect(() => {
        props.handlePaginationState({
            ...defaultPaginate,
            category_ids: "",
            location_ids: "",
        });
    }, []);

    useEffect(() => {
        setSearchText(pagination?.keywords || "");
    }, [pagination?.keywords]);

    const handleAddActivity = () => {
        props.changeActivityInitialState({
            showDrawer: true,
            formType: "Add",
        });
    };

    const handlePagination = (setting) => {
        props.handlePaginationState(setting);
    };

    const handleCategoryChange = (_, values) => {
        handlePagination({
            ...pagination,
            pageNo: 0,
            category_ids: values.map((item) => item.value).join(","),
        });
    };

    const handleLocationChange = (_, values) => {
        handlePagination({
            ...pagination,
            pageNo: 0,
            location_ids: values.map((item) => item.value).join(","),
        });
    };

    const handleResetFilters = () => {
        handlePagination({
            ...defaultPaginate,
            category_ids: "",
            location_ids: "",
        });
    };

    const selectedCategoryIds = pagination?.category_ids
        ? pagination.category_ids.split(",").filter(Boolean)
        : [];
    const selectedLocationIds = pagination?.location_ids
        ? pagination.location_ids.split(",").filter(Boolean)
        : [];
    const selectedCategories = categoryOptions.filter((option) => selectedCategoryIds.includes(option.value));
    const selectedLocations = locationOptions.filter((option) => selectedLocationIds.includes(option.value));
    const hasActiveFilters = Boolean(
        pagination?.keywords || pagination?.category_ids || pagination?.location_ids,
    );

    const handleDrawerClose = () => {
        setShowGenerateSlot(false);
        props.changeActivityInitialState({ showDrawer: false, formType: "", initialValues: null });
    };

    const handleGenerateSlot = (data) => {
        let payload = { ...data };
        delete payload.createdAt;
        setShowGenerateSlot(true);
        props.changeActivityInitialState({ initialValues: payload });
    };

    const handleActivityEdit = (data, type) => {
        let payload = { ...data };
        delete payload.createdAt;
        props.changeActivityInitialState({ showDrawer: true, formType: type, initialValues: payload });
    };

    return (
        <Stack spacing={1}>
            <Paper sx={{ marginBottom: "24px", padding: 1.5 }}>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6} sx={{ alignSelf: "center" }}>
                        <Typography variant="h6">List of Activity</Typography>
                    </Grid>
                    <Grid
                        item
                        xs={12}
                        md={6}
                        sx={{ alignSelf: "center" }}
                        display={"flex"}
                        flexDirection={"row"}
                        justifyContent={"flex-end"}
                    >
                        <HasPermission permission={PERMISSIONS.ADVANCE_MASTER.ACTIVITY.CREATE} fallback={null}>
                            <Button
                                disableElevation
                                variant="contained"
                                sx={{ borderRadius: "50px" }}
                                onClick={() => handleAddActivity()}
                            >
                                Add new Activity
                            </Button>
                        </HasPermission>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            size="small"
                            label="Search here..."
                            type="search"
                            fullWidth
                            value={searchText}
                            onChange={(e) => {
                                setSearchText(e.target.value);
                                debouncedSearchRef.current(e.target.value, pagination);
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <AutoCompleteSelect
                            multiple={true}
                            id="activity-category-filter"
                            options={categoryOptions}
                            label="Filter by Category"
                            onChange={handleCategoryChange}
                            value={selectedCategories}
                            name="activity_category_filter"
                            keyname="label"
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <AutoCompleteSelect
                            multiple={true}
                            id="activity-location-filter"
                            options={locationOptions}
                            label="Filter by Location"
                            onChange={handleLocationChange}
                            value={selectedLocations}
                            name="activity_location_filter"
                            keyname="label"
                        />
                    </Grid>
                    <Grid item xs={12} md={2} display="flex" justifyContent="flex-end">
                        <Button
                            disableElevation
                            variant="outlined"
                            fullWidth
                            disabled={!hasActiveFilters}
                            onClick={handleResetFilters}
                        >
                            Clear Filters
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            <Grid container>
                <Grid item xs={12}>
                    <Paper sx={{ p: 2, color: "#071B2A", fontWeight: "400" }} elevation={0}>
                        <ActivityTableComponent
                            handleGenerateSlot={handleGenerateSlot}
                            edit={(val, type) => handleActivityEdit(val, type)}
                            loading={isLoading}
                            fetching={isFetching}
                            count={activities?.count}
                            data={activities?.result || []}
                            pagination={pagination}
                            handlePagination={(val) => handlePagination(val)}
                        />
                    </Paper>
                </Grid>
            </Grid>
            <ActivityAddDrawer
                show={showDrawer}
                close={handleDrawerClose}
                formType={formType}
                initialValues={initialValues}
            />
            <GenerateBookingSlot
                show={showGenerateSlot}
                close={handleDrawerClose}
                formType={formType}
                initialValues={initialValues}
            />
        </Stack>
    );
};

export default ActivityManagerContainer;
